<template>
  <div class="flex flex-row w-full gap-2">
    <Card class="h-[71vh] mt-auto border-surface-300 dark:border-surface-600 shadow-none" pt:body:class="p-0!" pt:content:class="p-0!">
      <template #content>
        <div class="flex flex-col h-full w-[225px] gap-5">
          <div class="flex flex-row justify-center gap-2 py-3 bg-surface-100 dark:bg-surface-800 rounded-t-md">
            <p class="font-bold my-auto">Retainage %</p>
            <InputNumber
              class="h-8"
              input-class="w-16 text-center"
              v-model="retainagePercent"
              :min="0"
              :max="100"
              :allowEmpty="false"
              :disabled="!canEdit"
            />
          </div>
          <div class="flex flex-row w-full justify-evenly">
            <div class="flex flex-row gap-2">
              <p class="font-bold my-auto">Lender</p>
              <Checkbox v-model="lenderSelected" binary :disabled="!canEdit"></Checkbox>
            </div>
            <div class="flex flex-row justify-center gap-2">
              <p class="font-bold my-auto">Equity</p>
              <Checkbox v-model="equitySelected" binary :disabled="!canEdit"></Checkbox>
            </div>
          </div>
          <Button class="w-52 mx-auto" outlined label="Retain All Drawn Rows" :disabled="!canEdit" @click="clickRetainAll"></Button>
          <Button class="w-52 mx-auto" outlined label="Retain Selected Row(s)" :disabled="!canEdit" @click="clickRetainSelected"></Button>
          <Divider />
          <Button outlined class="w-52 mx-auto" label="Release All" severity="secondary" :disabled="!canEdit" @click="clickReleaseAll"></Button>
          <Button
            outlined
            class="w-52 mx-auto"
            label="Release Selected"
            severity="secondary"
            :disabled="!canEdit"
            @click="clickReleaseSelected"
          ></Button>
          <Divider />
          <div class="flex flex-row justify-center">
            <p class="font-bold my-auto">All Rows</p>
            <ToggleSwitch v-model="displayAllRows" :disabled="!canEdit" @change="getRetainageRows" class="mx-3" />
          </div>
        </div>
      </template>
    </Card>

    <div class="w-full">
      <DynamicTabs v-model:active-tab="activeTab" v-model:tabs="tabs" @tab-changed="getRetainageRows"></DynamicTabs>
      <div class="h-[71vh]">
        <ag-grid-vue
          theme="legacy"
          :rowData="retainageList"
          :columnDefs="colDefs"
          :defaultColDef="defaultColDefs"
          :autoSizeStrategy="{ type: 'fitGridWidth' }"
          singleClickEdit
          :getRowHeight="getRowHeight"
          stopEditingWhenCellsLoseFocus
          suppressDragLeaveHidesColumns
          rowSelection="multiple"
          class="h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          :doesExternalFilterPass="doesExternalFilterPass"
          :isExternalFilterPresent="isExternalFilterPresent"
          @grid-size-changed="resize"
          @cell-editing-stopped="cellEditComplete"
          @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
        ></ag-grid-vue>
      </div>
    </div>
  </div>

  <SubmitOptions
    class="mt-3"
    :submit-label="props.getRetainage ? 'Save' : 'Ok'"
    @cancelled="$emit('cancelled')"
    @submitted="submitRetainage"
  ></SubmitOptions>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import { type EntityRetainage, type Retainage } from "@/types/Retainage";
import type { Project } from "@/types/Projects";
import { currencyFormatter, formatCurrency, getCellEditableClass, getRetainage, round } from "@/services/Utility";
import type { MenuItem } from "primevue/menuitem";
import { release, retain, updateRetainageTotals } from "@/services/RetainageService";
import type { ProjectEntity } from "@/types/ProjectEntity";
import { useConfirm } from "primevue/useconfirm";
import type { CellEditingStoppedEvent, GridApi, GridReadyEvent, RowHeightParams, ValueFormatterParams, ValueParserParams } from "ag-grid-community";
import { useSystemStore } from "@/stores/systemStore";

const systemStore = useSystemStore();

const project = defineModel<Project>("project", { required: true });
const sourceOfFunds = defineModel<ProjectEntity[]>("sourceOfFunds", { required: true });
const entityRetainageList = defineModel<EntityRetainage[]>("entityRetainageList", { required: true });

interface Props {
  canEdit: boolean;
  getRetainage: boolean;
}
const props = defineProps<Props>();

const updateTabs = (): void => {
  tabs.value = [];

  entityRetainageList.value.forEach((entityRetainage: EntityRetainage) => {
    const item = {
      label: entityRetainage.projectEntityDisplay,
      icon: "",
      visible: true,
      key: entityRetainage.projectEntityId,
    };
    tabs.value.push(item);
  });

  getRetainageRows();
};

defineExpose({ updateTabs });

const confirm = useConfirm();

const retainageList = computed<Retainage[]>(() =>
  entityRetainageList.value.length === 0 ? [] : entityRetainageList.value[activeTab.value].retainageList
);

const lenderSelected = ref<boolean>(false);
const equitySelected = ref<boolean>(false);

const emit = defineEmits<{
  submitted: [];
  cancelled: [];
}>();

const activeTab = ref(0);
const displayAllRows = ref(true);

const tabs = ref<MenuItem[]>([]);
const retainagePercent = ref<number>(0);

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const getRowHeight = (params: RowHeightParams): number => {
  return params.node.rowPinned ? 25 : 32;
};

const resize = (): void => {
  gridApi.value?.sizeColumnsToFit();
};

const defaultColDefs = {
  floatingFilter: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  headerClass: "grid-align-center text-center",
  cellDataType: "text",
  sortable: false,
};

const retainParser = (params: ValueParserParams): number => {
  let input = Number(params.newValue);
  if (Number.isNaN(input)) return 0;
  if (entityRetainageList.value[activeTab.value].projectEntityDisplay.startsWith("L"))
    return round(input > params.data.thisDrawLoan ? params.data.thisDrawLoan : input < 0 ? 0 : input, 2);
  else return round(input > params.data.thisDrawEquity ? params.data.thisDrawEquity : input < 0 ? 0 : input, 2);
};

const releaseParser = (params: ValueParserParams): number => {
  let input = Number(params.newValue);
  if (Number.isNaN(input)) return 0;
  const maxRelease = params.data.totalRetained - params.data.totalReleasedPriorDraw + params.data.releasedThisDraw;
  return round(input > maxRelease ? maxRelease : input, 2);
};

const colDefs = ref([
  {
    field: "lineRefNumber",
    headerName: "Line #",
    minWidth: 100,
    cellClass: "text-center",
    headerCheckboxSelection: props.canEdit,
    checkboxSelection: props.canEdit,
  },
  {
    field: "description",
    headerName: "Description",
    minWidth: 150,
    width: 300,
  },
  {
    field: "thisDrawLoan",
    cellDataType: "number",
    headerName: "This Draw",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
    minWidth: 100,
  },
  {
    field: "thisDrawEquity",
    cellDataType: "number",
    headerName: "This Draw",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
    minWidth: 100,
    hide: true,
  },
  {
    field: "retainagePriorDraw",
    cellDataType: "number",
    headerName: "Retained Prior Draws",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
    minWidth: 100,
  },
  {
    field: "retainageThisDraw",
    cellDataType: "number",
    headerName: "Retained This Draw",
    cellClass: () => getCellEditableClass(),
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => retainParser(params),
    minWidth: 100,
    editable: props.canEdit,
  },
  {
    field: "releasedPriorDraw",
    cellDataType: "number",
    headerName: "Released Prior Draws",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
    minWidth: 100,
  },
  {
    field: "releasedThisDraw",
    cellDataType: "number",
    headerName: "Released This Draw",
    cellClass: () => getCellEditableClass(),
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => releaseParser(params),
    minWidth: 100,
    editable: props.canEdit,
  },
  {
    field: "totalRetainage",
    cellDataType: "number",
    headerName: "Total Retained",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
    minWidth: 100,
  },
  {
    field: "totalReleased",
    cellDataType: "number",
    headerName: "Total Released",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
    minWidth: 100,
  },
  {
    field: "retainageBalance",
    cellDataType: "number",
    headerName: "Retainage Balance",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
    minWidth: 100,
  },
]);

const isExternalFilterPresent = (): boolean => {
  return !displayAllRows.value;
};

const doesExternalFilterPass = (node: any): boolean => {
  const r = node.data;

  return (
    r.retainagePriorDraw !== 0 ||
    r.retainageThisDraw !== 0 ||
    r.totalRetainagePriorDraw !== 0 ||
    r.totalRetainage !== 0 ||
    r.releasedPriorDraw !== 0 ||
    r.releasedThisDraw !== 0 ||
    r.totalReleasedPriorDraw !== 0 ||
    r.totalReleased !== 0 ||
    r.retainageBalance !== 0
  );
};

const clickRetainAll = (): void => {
  const indexes: number[] = [...Array(retainageList.value.length).keys()];
  retain(
    indexes,
    entityRetainageList.value,
    sourceOfFunds.value,
    lenderSelected.value,
    equitySelected.value,
    retainagePercent.value / 100,
    project.value.projectSettings
  );
  gridApi.value?.setGridOption("rowData", entityRetainageList.value[activeTab.value].retainageList);
};

const clickRetainSelected = (): void => {
  const selectedNodes = gridApi.value?.getSelectedNodes();
  if (selectedNodes == null || selectedNodes.length === 0) {
    error("Invalid Input", "No rows are selected to be retained.");
    return;
  }

  const indexes: number[] = selectedNodes.flatMap((node: any) => parseInt(node.id)).sort((a: number, b: number) => a - b);
  retain(
    indexes,
    entityRetainageList.value,
    sourceOfFunds.value,
    lenderSelected.value,
    equitySelected.value,
    retainagePercent.value / 100,
    project.value.projectSettings
  );
  gridApi.value?.setGridOption("rowData", entityRetainageList.value[activeTab.value].retainageList);
};

const clickReleaseAll = (): void => {
  const indexes: number[] = [...Array(retainageList.value.length).keys()];
  const message = `Are you sure you want to release retainage for ${entityRetainageList.value[activeTab.value].projectEntityDisplay} on all items?`;
  confirmRelease(indexes, message);
};

const clickReleaseSelected = (): void => {
  const selectedNodes = gridApi.value?.getSelectedNodes();
  if (selectedNodes == null || selectedNodes.length === 0) {
    error("Invalid Input", "No rows are selected to be released.");
    return;
  }

  const indexes: number[] = selectedNodes.flatMap((node: any) => parseInt(node.id)).sort((a: number, b: number) => a - b);
  const message = `Are you sure you want to release retainage for ${entityRetainageList.value[activeTab.value].projectEntityDisplay} on ${
    indexes.length
  } items?`;
  confirmRelease(indexes, message);
};

const confirmRelease = (indexes: number[], message: string): void => {
  confirmPopup(
    confirm,
    "Release Rows",
    message,
    () => {
      release(indexes, entityRetainageList.value[activeTab.value]);
      gridApi.value?.setGridOption("rowData", entityRetainageList.value[activeTab.value].retainageList);
    },
    () => info("Release Cancelled", "")
  );
};

const cellEditComplete = (event: CellEditingStoppedEvent): void => {
  const colId = event.colDef.field ?? "";
  if (colId === "retainageThisDraw") {
    const drawItemId = event.data.drawItemId;
    const isLender = entityRetainageList.value[activeTab.value].projectEntityDisplay.startsWith("L");
    let totalRetained = 0;
    let maxAllowed = 0;
    entityRetainageList.value.forEach((er) => {
      if ((isLender && !er.projectEntityDisplay.startsWith("L")) || (!isLender && !er.projectEntityDisplay.startsWith("B"))) return;
      const retainage = er.retainageList.find((retainage) => retainage.drawItemId === drawItemId);
      if (retainage) {
        totalRetained += retainage.retainageThisDraw;
        maxAllowed = isLender ? retainage.thisDrawLoan : retainage.thisDrawEquity;
      }
    });

    if (totalRetained > maxAllowed) {
      error(
        "Invalid Retainage Amount",
        `Unable to retain more (${formatCurrency(totalRetained)}) than allocated funds (${formatCurrency(maxAllowed)}).`
      );
      event.node.setDataValue("retainageThisDraw", event.oldValue);
    }
  }

  updateRetainageTotals(event.data);
};

const submitRetainage = (): void => {
  if (props.getRetainage) {
    systemStore.startLoading("Updating Retainage...");
    axios
      .post("/retainage/update/" + project.value.projectDrawInfo.drawId, entityRetainageList.value)
      .then((response) => {
        handleApiResponse(
          response,
          (data: any) => {
            emit("submitted");
          },
          (errors: ApiError[]) => {
            error("An error occurred", errors[0].message);
          }
        );
      })
      .catch((e) => errorApi(e))
      .finally(() => systemStore.stopLoading());
  } else {
    emit("submitted");
  }
};

const getRetainageRows = (): void => {
  gridApi.value?.onFilterChanged();
  gridApi.value?.setColumnsVisible(["thisDrawLoan"], entityRetainageList.value[activeTab.value].projectEntityDisplay.startsWith("L"));
  gridApi.value?.setColumnsVisible(["thisDrawEquity"], entityRetainageList.value[activeTab.value].projectEntityDisplay.startsWith("B"));
  resize();
};

onBeforeMount(async (): Promise<void> => {
  if (props.getRetainage) {
    await getRetainage(project.value.projectDrawInfo.drawId).then((retainage) => (entityRetainageList.value = retainage));
    updateTabs();
  }
});
</script>
